
import { computed, ref, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { UserDataField, UserDataRequirements, UserInformation } from '@/types/user-data';
import Button from '@/components/Button.vue';
import TermsAndConditionsCheck from '@/components/TermsAndConditionsCheck.vue';
import { useStore } from 'vuex';
import { Credentials } from '@/store/auth/types';
import { Client } from 'altru-types';
import { RouteNames } from '@/router';
import { useRouter } from 'vue-router';

export default {
  name: 'UserData',
  components: {
    Button,
    TermsAndConditionsCheck,
  },
  setup: (
    _props: Record<string, never>,
  ): {
    email: Ref<string>;
    firstName: Ref<string>;
    lastName: Ref<string>;
    jobTitle: Ref<string>;
    requirements: UserDataRequirements;
    onSubmit: (e: Event) => Promise<void | boolean>;
    onChange: (e: Event) => void;
    t: (key: string) => string;
    clickWrapFeatureEnabled: Ref<boolean>;
  } => {
    const { t } = useI18n();
    const { push } = useRouter();
    const { getters, commit, dispatch } = useStore();
    const credentials: Ref<Credentials> = computed(() => getters['auth/credentialsValue']);
    const client: Ref<Client | null> = computed(() => getters['clients/workspaceInfo']);
    const clickWrapFeatureEnabled: Ref<boolean> = computed(
      () => client.value?.feature_keys?.includes('click_wrap_terms_and_conditions') ?? false,
    );
    const userInformationCollected: Ref<UserInformation | null> = computed(
      () => getters['users/userInformationCollected'],
    );
    const workspaceTermsAcceptedAt: Ref<string | null> = computed(() => getters['clients/workspaceTermsAcceptedAt']);
    const isIframed: Ref<boolean> = computed(() => getters['status/isIframed']);

    /* TODO: uncomment the following code to fill the user data form automatically based on values got from admin credentials
    watch(
      () => credentials.value,
      (setCredentials) => {
        if (setCredentials) {
          email.value = setCredentials.uid ?? '';
        }
      },
    );

    const userInfo: Ref<User> = computed(() => store.getters['users/currentUser']);
    watch(
      () => userInfo.value,
      (user) => {
        if (user) {
          email.value = user.email ?? email.value;
          firstName.value = user.first_name ?? firstName.value;
          lastName.value = user.last_name ?? lastName.value;
          jobTitle.value = user.job_title ?? jobTitle.value;
        }
      },
    );*/

    const email: Ref<string> = ref(userInformationCollected.value?.email ?? '');
    const firstName: Ref<string> = ref(userInformationCollected.value?.firstName ?? '');
    const lastName: Ref<string> = ref(userInformationCollected.value?.lastName ?? '');
    const jobTitle: Ref<string> = ref(userInformationCollected.value?.jobTitle ?? '');

    const isRequired = (field: UserDataField, client: Client | null): boolean => {
      switch (field) {
        case 'email':
          return client?.feature_hash?.recording_form_fields?.required.includes('email') === true;
        case 'firstName':
          return client?.feature_hash?.recording_form_fields?.required.includes('first_name') === true;
        case 'lastName':
          return client?.feature_hash?.recording_form_fields?.required.includes('last_name') === true;
        case 'jobTitle':
          return client?.feature_hash?.recording_form_fields?.required.includes('job_title') === true;
        default: {
          const unknownField: never = field;
          console.error(`Unknown user data field: "${unknownField}""`);
          return false;
        }
      }
    };
    const isEnabled = (field: UserDataField, client: Client | null): boolean => {
      switch (field) {
        case 'email':
          return (
            !!client?.feature_hash?.recording_form_fields?.required.includes('email') ||
            !!client?.feature_hash?.recording_form_fields?.optional.includes('email')
          );
        case 'firstName':
          return (
            !!client?.feature_hash?.recording_form_fields?.required.includes('first_name') ||
            !!client?.feature_hash?.recording_form_fields?.optional.includes('first_name')
          );
        case 'lastName':
          return (
            !!client?.feature_hash?.recording_form_fields?.required.includes('last_name') ||
            !!client?.feature_hash?.recording_form_fields?.optional.includes('last_name')
          );
        case 'jobTitle':
          return (
            !!client?.feature_hash?.recording_form_fields?.required.includes('job_title') ||
            !!client?.feature_hash?.recording_form_fields?.optional.includes('job_title')
          );
        default: {
          const unknownField: never = field;
          console.error(`Unknown user data field: "${unknownField}""`);
          return false;
        }
      }
    };

    const requirements: UserDataRequirements = {
      email: {
        required: isRequired('email', client.value),
        enabled: isEnabled('email', client.value),
      },
      firstName: {
        required: isRequired('firstName', client.value),
        enabled: isEnabled('firstName', client.value),
      },
      lastName: {
        required: isRequired('lastName', client.value),
        enabled: isEnabled('lastName', client.value),
      },
      jobTitle: {
        required: isRequired('jobTitle', client.value),
        enabled: isEnabled('jobTitle', client.value),
      },
    };

    const onSubmit = async (e: Event): Promise<boolean | void> => {
      e.preventDefault();
      const userInformation: UserInformation = {
        email: email.value,
        firstName: firstName.value,
        lastName: lastName.value,
        jobTitle: jobTitle.value,
      };

      if (!clickWrapFeatureEnabled.value && !workspaceTermsAcceptedAt.value) {
        return await dispatch('ui/setErrorMessage', { value: t('TERMS AND CONDITIONS REQUIRED'), variant: 'warn' });
      }

      commit('users/setUserInfoCollected', userInformation);

      await push({
        name: !clickWrapFeatureEnabled.value || isIframed.value ? RouteNames.Camera : RouteNames.Terms,
      });
    };

    const onChange = (e: Event) => {
      if (e.target) {
        const input = e.target as HTMLInputElement;
        // If the input form changed is the email, enable anonymous flow if not the same on retrieved form admin
        if (input.id === 'email') {
          if (input.value !== credentials.value?.uid) {
            commit('auth/toggleAnonymousFlow');
          } else {
            commit('auth/toggleAnonymousFlow', false);
          }
        } else {
          // Enable anonymous flow as soon as any other field is modified; roll it back if empty
          if (input.value !== '') {
            commit('auth/toggleAnonymousFlow');
          } else {
            commit('auth/toggleAnonymousFlow', false);
          }
        }
      }
    };

    return {
      email,
      firstName,
      lastName,
      jobTitle,
      requirements,
      onSubmit,
      onChange,
      t,
      clickWrapFeatureEnabled,
    };
  },
};
